/**
 * Login Component
 * Handles user authentication with 2FA support and Regtank onboarding
 */

import { Text, Modal } from "@mantine/core";
import {
  InputGroup,
  ForgotPasswordInput,
  LargeBtn,
  color,
} from "@funded-here-interface/common";
import { ChangeEvent, FC, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setInvestorToken } from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import { useStyles } from "./LoginCss";
import { ToastContainer, toast } from "react-toastify";
import useLoginMutation from "@funded-here-interface/common/src/hooks/useLogin";
import "react-toastify/dist/ReactToastify.css";
import Flogo from "../../assets/Funded_Here_LogoA4.svg";
import { Persona } from "@funded-here-interface/common/src/constant/enum";
import useGetUser from "@funded-here-interface/common/src/hooks/useGetUsers";
import { useDisclosure } from "@mantine/hooks";
import useVerifyTwoFAToken from "@funded-here-interface/common/src/hooks/useVerifyTwoFAToken";
import OtpField from "@funded-here-interface/common/src/components/OtpField/OtpField";
import { RootState } from "../../store";
import useResendOtp from "../../hooks/useResendOtp";
import { RegtankOnboardingModal } from "@funded-here-interface/common/src/components/Regtank/RegtankOnboardingModel";
import { useBuildRegtankOnboardModel } from "@funded-here-interface/common/src/hooks/useBuildRegtankOnboardModel";
import { useNavigateAfterRegtank } from "@funded-here-interface/common/src/hooks/useNavigateAfterRegtank";
import { useRegtankOnboardingClick } from "@funded-here-interface/common/src/hooks/useRegtankOnboardingClick";
import { useSessionCriticalModal } from "@funded-here-interface/common/src/hooks/useSessionCriticalModal";

// Custom hooks
import { usePostAuthenticationFlow } from "./hooks/usePostAuthenticationFlow";
import { validateLoginForm } from "./utils/validation";

const Login: FC = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [persona, setPersona] = useState("");
  const [error, setError] = useState({
    email_error: "",
    pass_error: "",
    persona_error: "",
  });

  // Modal state
  const [openedVerifyOTP, modalAction] = useDisclosure(false);
  const [openedRegtankOnboarding, modalActionRegtank] = useDisclosure(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [pendingOnBoardingAs, setPendingOnBoardingAs] = useState("");

  // Redux state
  const { token } = useSelector((state: RootState) => state.auth);

  // API hooks
  const mutation = useLoginMutation();
  const getUserMutation = useGetUser();
  const verifyTwoFATokenMutation = useVerifyTwoFAToken();
  const resendOtpMutation = useResendOtp();

  // Regtank onboarding hooks
  const { handleNavigateNext } = useNavigateAfterRegtank();
  const modalContent = useBuildRegtankOnboardModel(pendingOnBoardingAs);
  const { isLoadingRegtank, setRegtankOnboardingUrl, handleRegtankClick } =
    useRegtankOnboardingClick(token);

  // Post-authentication flow hook
  const { handlePostAuthentication } = usePostAuthenticationFlow({
    setPendingOnBoardingAs,
    setRegtankOnboardingUrl,
    handleNavigateNext,
  });

  // Register Regtank modal as session-critical to prevent JWT logout while open
  useSessionCriticalModal("regtank-ekyc", openedRegtankOnboarding);

  /**
   * Handle user authentication after successful login/2FA
   */
  const handleUserAuthentication = (authToken: string) => {
    sessionStorage.setItem("Auth", authToken);
    dispatch(setInvestorToken({ token: authToken }));

    getUserMutation.mutate(
      { token: authToken },
      {
        onSuccess: (data: any) => {
          handlePostAuthentication(data.user, modalActionRegtank.open);
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  /**
   * Handle login form submission
   */
  const handleLogin = async () => {
    const validation = validateLoginForm(email, password, persona);

    if (!validation.isValid) {
      if (!persona) {
        toast.error("Select a persona");
      }
      setError(validation.errors);
      return;
    }

    mutation.mutate(
      { email, password, persona },
      {
        onSuccess: async (data) => {
          if (!data) return;

          // Handle 2FA setup flow
          if (data.stage === "setup2FA") {
            const dataForQRCode = {
              email,
              persona,
              role: "Investor",
            };
            navigate("/two-auth-QR-code", { state: dataForQRCode });
            return;
          }

          // Handle 2FA verification flow
          if (data.stage === "verifyOTP") {
            modalAction.open();
            return;
          }

          // Handle successful login
          handleUserAuthentication(data);
        },
        onError: (e) => {
          const errorMessage = (e as Error).message;

          // Handle unverified user - resend OTP and redirect to verification page
          if (errorMessage.toLowerCase().includes("unverified")) {
            resendOtpMutation.mutate(
              { email },
              {
                onSuccess: () => {
                  toast.success("Verification code sent to your email");
                  navigate("/otp-verification", { state: { email } });
                },
                onError: () => {
                  // Even if resend fails, still navigate to OTP page
                  // User can manually resend from there
                  navigate("/otp-verification", { state: { email } });
                },
              }
            );
            return;
          }

          toast.error(errorMessage);
        },
      }
    );
  };

  /**
   * Handle persona/role selection change
   */
  const handlePersonaChange = (e: any) => {
    setPersona(e.target.value);
    setError((prevState) => ({ ...prevState, persona_error: "" }));
  };

  /**
   * Handle OTP input change
   */
  const handleOtpChange = (value: string) => {
    setOtp(value);
    setOtpError("");
  };

  /**
   * Handle 2FA OTP verification
   */
  const handleVerifyOtp = async () => {
    if (!otp) return;

    verifyTwoFATokenMutation.mutate(
      { token: otp, email, persona },
      {
        onSuccess: (data) => {
          if (data) {
            handleUserAuthentication(data);
          }
        },
        onError: (e) => {
          if (
            (e as Error).message ===
            "User is not verified for two factor authentication"
          ) {
            setOtpError("The code you entered is incorrect. Please try again.");
          } else {
            toast.error((e as Error).message);
          }
        },
      }
    );
  };

  return (
    <>
      <div className={classes.gradientContainer}></div>
      <div className={classes.parentContainer}>
        <div className={classes.loginContainer}>
          <div className={classes.loginHeadingsub}>
            <div>
              <img
                src={Flogo}
                className={classes.logo}
                alt="Funded Here Logo"
              />
            </div>
          </div>
          <div className={classes.inputGroupContainer}>
            <div style={{ marginBottom: "20px" }}>
              <Text className={classes.roleLabel}>Select Role</Text>
              <div className={classes.radioButtons}>
                <label className={classes.label}>
                  <input
                    type="radio"
                    name="persona"
                    value="investor"
                    checked={persona === Persona.INVESTOR}
                    onChange={handlePersonaChange}
                  />
                  Investor
                </label>
                <label className={classes.label}>
                  <input
                    type="radio"
                    name="persona"
                    value="merchant"
                    checked={persona === Persona.MERCHANT}
                    onChange={handlePersonaChange}
                  />
                  Merchant
                </label>
                <label className={classes.label}>
                  <input
                    type="radio"
                    name="persona"
                    value="distributor"
                    checked={persona === Persona.DISTRIBUTOR}
                    onChange={handlePersonaChange}
                  />
                  Supplier
                </label>
                <label className={classes.label}>
                  <input
                    type="radio"
                    name="persona"
                    value="others"
                    checked={persona === Persona.OTHERS}
                    onChange={handlePersonaChange}
                  />
                  Others
                </label>
              </div>
            </div>
            <div className={classes.inputstyle}>
              <InputGroup
                labelText="Email"
                id="your-email"
                value={email}
                placeHolder="Enter Email Address"
                onChange={(value: string) => setEmail(value)}
                isErrored={error.email_error}
              />
            </div>

            <div className={classes.inputstyle}>
              <ForgotPasswordInput
                className="LoginForgotPasswordInput"
                name="password"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setPassword(e.target.value.trim());
                }}
                forgotPassword={() => navigate("/password-reset")}
                isErrored={error.pass_error}
              />
            </div>

            <div className={classes.buttonDiv}>
              <input
                className={classes.input}
                type="submit"
                value="Login"
                onClick={handleLogin}
              />
            </div>
            <div className={classes.signInFooter}>
              <Text className={classes.noAccount}>Don't have an account?</Text>

              <Text
                className={classes.signUp}
                onClick={() => navigate("/registration")}
              >
                Sign up
              </Text>
            </div>
            <ToastContainer />
          </div>
        </div>
      </div>
      <Modal
        classNames={{
          header: classes.modalHeader,
          title: classes.modalTitle,
        }}
        opened={openedVerifyOTP}
        onClose={modalAction.close}
        title="Two-Factor Authentication"
        color="gray"
        centered
        overlayProps={{
          blur: 2,
          opacity: 0.1,
        }}
      >
        <OtpField
          length={6}
          onChange={handleOtpChange}
          onEnter={handleVerifyOtp}
        />
        <div
          style={{
            textAlign: "center",
            fontSize: "10px",
            marginBottom: "20px",
          }}
        >
          {otpError && <span style={{ color: "red" }}>{otpError}</span>}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
            marginBottom: "50px",
          }}
        >
          <LargeBtn
            onClick={handleVerifyOtp}
            backgroundColor={color.FHGREEN}
            textColor="#FFFFFF"
            width="5%"
            disabled={!otp}
          >
            Verify
          </LargeBtn>
        </div>
      </Modal>
      <RegtankOnboardingModal
        opened={openedRegtankOnboarding}
        onClose={modalActionRegtank.close}
        title={modalContent.regtankModalTitle}
        firstLine={modalContent.regtankFirstLine}
        secondLine={modalContent.regtankSecLine}
        onButtonClick={handleRegtankClick}
        isLoadingOnboarding={isLoadingRegtank}
      />
    </>
  );
};

export default Login;
